import { useState, useMemo } from "react";
import { TASK_STATUSES } from "../utils/constants";
import { fmtDMY } from "../utils/date";
import { ColoredSpan } from "./ColoredSpan";

/**
 * Таблица задач с сортировкой по столбцам.
 *
 * treeMode={true} отключает сортировку и добавляет отступ слева
 * в зависимости от task.depth, позволяя отображать иерархию задач
 * (дерево) в виде "лесенки". Отступы задаются классами в CSS
 * (.tasks-table td.task-depth-N), без использования inline-стилей
 * и без !important.
 */
export const TaskTable = ({
  tasks,
  onRowClick,
  columns = ["title", "assignee", "status", "planned", "fact", "deadline"],
  db,
  getTaskSpent,
  empName,
  showProject = false,
  treeMode = false,
}) => {
  const [sortField, setSortField] = useState("title");
  const [sortDir, setSortDir] = useState("asc");

  const projectsById = useMemo(
    () => new Map((db?.projects || []).map((p) => [p.id, p])),
    [db?.projects],
  );

  const sortedTasks = useMemo(() => {
    if (treeMode) return tasks;

    const keyed = tasks.map((t) => {
      let key;
      switch (sortField) {
        case "title":
          key = (t.title || "").toLowerCase();
          break;
        case "assignee":
          key = (t.assigneeId ? empName(t.assigneeId) : "").toLowerCase();
          break;
        case "status":
          key = TASK_STATUSES[t.status]?.label || t.status || "";
          break;
        case "planned":
          key = t.plannedHours ?? -1;
          break;
        case "fact":
          key = getTaskSpent(t);
          break;
        case "deadline":
          key = t.deadline || "";
          break;
        case "project":
          key = projectsById.get(t.projectId)?.code || "";
          break;
        default:
          key = "";
      }
      return { task: t, key };
    });

    keyed.sort((a, b) => {
      if (a.task._draft !== b.task._draft) return a.task._draft ? -1 : 1;
      if (a.key < b.key) return sortDir === "asc" ? -1 : 1;
      if (a.key > b.key) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return keyed.map((k) => k.task);
  }, [
    tasks,
    treeMode,
    sortField,
    sortDir,
    projectsById,
    empName,
    getTaskSpent,
  ]);

  const handleSort = (field) => {
    if (sortField === field)
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortableHeader = (field, label) => (
    <th
      className={treeMode ? "" : "sortable"}
      onClick={treeMode ? undefined : () => handleSort(field)}
    >
      {label}{" "}
      {!treeMode && sortField === field && (sortDir === "asc" ? "↑" : "↓")}
    </th>
  );

  return (
    <div className="tasks-table-wrap">
      <table className="tbl tasks-table">
        <thead>
          <tr>
            {columns.includes("title") && sortableHeader("title", "Задача")}
            {columns.includes("assignee") &&
              sortableHeader("assignee", "Исполнитель")}
            {columns.includes("status") && sortableHeader("status", "Статус")}
            {columns.includes("planned") &&
              sortableHeader("planned", "План (ч)")}
            {columns.includes("fact") && sortableHeader("fact", "Факт (ч)")}
            {columns.includes("deadline") &&
              sortableHeader("deadline", "Дедлайн")}
            {showProject &&
              columns.includes("project") &&
              sortableHeader("project", "Проект")}
          </tr>
        </thead>
        <tbody>
          {sortedTasks.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="mut text-center">
                Нет задач
              </td>
            </tr>
          ) : (
            sortedTasks.map((task) => {
              const isSyntheticDraft = task._draft === true;
              return (
                <tr
                  key={task.id}
                  className={isSyntheticDraft ? "draft-row" : "clickable-row"}
                  onClick={
                    isSyntheticDraft ? undefined : () => onRowClick(task.id)
                  }
                >
                  {columns.includes("title") && (
                    <td
                      className={`task-depth-${Math.min(task.depth || 0, 5)}`}
                    >
                      <b>{task.title}</b>
                      {isSyntheticDraft && (
                        <span className="task-draft-badge">из шаблона</span>
                      )}
                    </td>
                  )}
                  {columns.includes("assignee") && (
                    <td>{task.assigneeId ? empName(task.assigneeId) : "-"}</td>
                  )}
                  {columns.includes("status") && (
                    <td>
                      <ColoredSpan
                        color={TASK_STATUSES[task.status]?.color || "#64748b"}
                        className="st-chip st-chip--tinted"
                      >
                        {TASK_STATUSES[task.status]?.label || task.status}
                      </ColoredSpan>
                    </td>
                  )}
                  {columns.includes("planned") && (
                    <td>{task.plannedHours ?? "-"}</td>
                  )}
                  {columns.includes("fact") && <td>{getTaskSpent(task)}</td>}
                  {columns.includes("deadline") && (
                    <td>{task.deadline ? fmtDMY(task.deadline) : "-"}</td>
                  )}
                  {showProject && columns.includes("project") && (
                    <td>{projectsById.get(task.projectId)?.code || "-"}</td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};
